import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const Stat = ({ label, value, sub, accent }) => (
  <div className={`rounded-2xl p-5 border ${accent}`}>
    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs mt-1 text-gray-400">{sub}</p>}
  </div>
);

const Toggle = ({ label, enabled }) => (
  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3.5">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
      {enabled ? "Enabled" : "Disabled"}
    </span>
  </div>
);

const CompanyConfigCard = ({ serviceType, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const { callApi } = useApi(`/commission/company-config/service/${serviceType}`, "GET");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await callApi();
      setData(res?.data || res);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (serviceType) fetchData(); }, [serviceType]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-base font-bold text-gray-900">Company Rate Config</h2>
          <p className="text-xs text-gray-400 mt-0.5">Global commission caps for <span className="font-semibold text-gray-600">{serviceType}</span></p>
        </div>
        <button
          onClick={() => onEdit(data)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562] transition shadow-sm"
        >
          {data ? "✎ Edit Config" : "+ Set Config"}
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-12 text-center">
          <div className="w-8 h-8 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading config...</p>
        </div>
      ) : !data ? (
        <div className="p-14 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-3xl mx-auto mb-4">⚙</div>
          <p className="text-gray-500 text-sm font-medium mb-1">No configuration set</p>
          <p className="text-gray-400 text-xs mb-5">Set global caps before configuring user rates</p>
          <button onClick={() => onEdit(null)} className="px-5 py-2.5 rounded-xl bg-[#0A2A4F] text-white text-sm font-bold hover:bg-[#0d3562] transition">
            Set Config Now
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Max Agent Charge" value={`₹${data.maxAgentCharge ?? "—"}`} accent="border-blue-100 bg-blue-50/50" />
            <Stat label="Max Total Commission" value={`₹${data.maxTotalCommission ?? "—"}`} accent="border-emerald-100 bg-emerald-50/50" />
            <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Commissions</p>
              <Toggle label="Merchant" enabled={data.enableMerchantCommission} />
              <div className="mt-2">
                <Toggle label="MD" enabled={data.enableMdCommission} />
              </div>
            </div>
            {data.description && (
              <div className="rounded-2xl border border-gray-100 bg-amber-50/50 p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Note</p>
                <p className="text-sm text-gray-600 italic leading-relaxed">{data.description}</p>
              </div>
            )}
          </div>
          <div className="flex gap-4 text-xs text-gray-400 pt-1 border-t border-gray-100">
            <span>Created: {data.createdAt ? new Date(data.createdAt).toLocaleString() : "—"}</span>
            <span>Updated: {data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "—"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyConfigCard;
